import { useAutoAnimate } from '@formkit/auto-animate/react';
import { cn } from 'cn';
import { memo, type ReactElement, useCallback } from 'react';

import { ComponentTestIds } from '~/constants/test-ids';

import { Spinner } from '@vestigas/ui/molecules';
import { withErrorBoundary } from '~/ui/atoms';
import { VirtualizedListServerDriven } from '~/ui/molecules/VirtualizedList';

import {
  Column,
  ColumnHeader,
  CountIndicator,
  ListActions,
  ManageableListItem,
  MultiItemsManagerHeader,
  NoResults,
  SelectedItemsDisplayMode,
  Triangle,
} from './components';
import { type Item, type MultiItemsManagerProps } from './types';
import { useMultiItemsManager } from './useMultiItemsManager';
import { defaultSelectFunction } from './utils';

/**
 * Component for managing a list of selected items.
 * Optimized for managing large lists of items with features including:
 * - Search functionality
 * - Visual indicators for added/removed items
 * - Server-side pagination support and virtualized list rendering with infinite scroll
 * - Edit and display modes
 */
export const MultiItemsManager = withErrorBoundary(
  memo(
    ({
      fieldName = 'name',
      getIsItemLocked,
      getterFunctionSelectableItems,
      getterFunctionSelectedItems,
      isDisabled = false,
      label,
      lockedItemTooltip,
      onChange,
      onSaveSelection,
      onSelectedItemClick,
      openSelectTestId,
      renderSelectedChip,
      selectFunctionSelectableItems = defaultSelectFunction,
      selectFunctionSelectedItems = defaultSelectFunction,
    }: MultiItemsManagerProps) => {
      const {
        addedItems,
        addedItemsCount,
        fetchNextPageSelectableItems,
        fetchNextPageSelectedItems,
        handleAdd,
        handleCancel,
        handleDelete,
        handleEdit,
        handleOpenBody,
        handleSave,
        hasNextPageSelectableItems,
        hasNextPageSelectedItems,
        isEditing,
        isFetchingNextPageSelectableItems,
        isFetchingNextPageSelectedItems,
        isLoadingSelectedItems,
        isOpen,
        removedItems,
        removedItemsCount,
        searchInputRef,
        searchString,
        selectableItems,
        selectableItemsFiltered,
        selectedItems,
        selectedItemsFiltered,
        selectedItemsTotalCount,
        setSearchString,
      } = useMultiItemsManager({
        fieldName,
        getterFunctionSelectableItems,
        getterFunctionSelectedItems,
        onChange,
        onSaveSelection,
        selectFunctionSelectableItems,
        selectFunctionSelectedItems,
      });

      const [bodyAnimationRef] = useAutoAnimate({
        duration: 200,
        easing: 'ease-out',
      });
      const [columnsAnimationRef] = useAutoAnimate({
        duration: 200,
        easing: 'ease-out',
      });

      const hasNoMatchingSelectedItems =
        searchString && selectedItemsFiltered.length === 0;

      const hasNoMatchingSelectableItems =
        searchString && selectableItems?.length === 0;

      const getChangesString = ({
        addedItemsCount,
        removedItemsCount,
      }: {
        addedItemsCount: number;
        removedItemsCount: number;
      }) => {
        if (addedItemsCount + removedItemsCount === 0) {
          return '';
        }

        return `(${[
          addedItemsCount > 0 && `${addedItemsCount} hinzugefügt`,
          removedItemsCount > 0 && `${removedItemsCount} entfernt`,
        ]
          .filter(Boolean)
          .join(', ')})`;
      };

      const getItemType = useCallback(
        (item: Item): Item['type'] => {
          if (addedItems.includes(item)) {
            return 'added';
          }

          if (removedItems.includes(item)) {
            return 'removed';
          }

          return 'current';
        },
        [addedItems, removedItems],
      );

      const renderSelectedItem = (item: Item): ReactElement => (
        <ManageableListItem
          fieldName={fieldName}
          isLocked={getIsItemLocked?.(item)}
          item={item}
          lockedTooltip={lockedItemTooltip}
          type={getItemType(item)}
          variant="selected"
          onClick={handleDelete}
        />
      );

      const renderSelectableItem = (item: Item): ReactElement => (
        <ManageableListItem
          fieldName={fieldName}
          item={item}
          type={getItemType(item)}
          variant="selectable"
          onClick={handleAdd}
        />
      );

      return (
        <div className="rounded-md border border-tiga-border-lighter bg-white">
          <div>
            <MultiItemsManagerHeader
              isOpen={isOpen}
              testId={openSelectTestId}
              onOpen={handleOpenBody}
            >
              <CountIndicator>
                {isLoadingSelectedItems ? <Spinner /> : selectedItemsTotalCount}
              </CountIndicator>
              <div>
                {label}{' '}
                {getChangesString({ addedItemsCount, removedItemsCount })}
              </div>
            </MultiItemsManagerHeader>
            <div ref={bodyAnimationRef}>
              {isOpen ? (
                <>
                  <ListActions
                    ref={searchInputRef}
                    editButtonTestId={
                      ComponentTestIds.MULTI_SELECT
                        .OPEN_EDIT_MULTI_SELECT_BUTTON
                    }
                    hasChanges={addedItemsCount + removedItemsCount > 0}
                    isDisabled={isDisabled}
                    isEditing={isEditing}
                    saveButtonTestId={
                      ComponentTestIds.MULTI_SELECT.SAVE_MULTI_SELECT_BUTTON
                    }
                    searchString={searchString}
                    setSearchString={setSearchString}
                    onCancel={handleCancel}
                    onEdit={handleEdit}
                    onFocus={() => {
                      handleOpenBody(true);
                    }}
                    onSave={handleSave}
                  />
                  <div
                    ref={columnsAnimationRef}
                    className="flex max-h-[50vh] flex-1"
                  >
                    <Column
                      className={cn('flex max-h-96 min-h-40 flex-1', {
                        'p-2': !isEditing,
                        'w-1/2': isEditing,
                      })}
                    >
                      {isEditing ? (
                        <ColumnHeader>Zugeordnete {label}</ColumnHeader>
                      ) : null}
                      <>
                        {isEditing ? (
                          <VirtualizedListServerDriven
                            className={
                              hasNoMatchingSelectedItems ? 'h-0' : 'h-96'
                            }
                            fetchNextPage={fetchNextPageSelectedItems}
                            hasNextPage={hasNextPageSelectedItems}
                            isFetchingNextPage={isFetchingNextPageSelectedItems}
                            itemHeight={48}
                            items={selectedItemsFiltered}
                            renderItem={(item) => renderSelectedItem(item)}
                            totalCount={selectedItemsFiltered.length}
                          />
                        ) : (
                          <SelectedItemsDisplayMode
                            className={
                              hasNoMatchingSelectedItems ? 'flex h-0' : 'h-full'
                            }
                            currentItems={selectedItems ?? []}
                            currentItemsFiltered={selectedItemsFiltered}
                            fetchNextPage={fetchNextPageSelectedItems}
                            hasNextPage={hasNextPageSelectedItems}
                            isFetchingNextPage={isFetchingNextPageSelectedItems}
                            label={label}
                            renderItem={renderSelectedChip}
                            onSelectedItemClick={onSelectedItemClick}
                          />
                        )}
                        {hasNoMatchingSelectedItems ? (
                          <NoResults searchString={searchString} />
                        ) : null}
                      </>
                    </Column>
                    {isEditing ? (
                      <>
                        <Column className="min-w-fit items-center justify-center border-r border-l border-gray-200 p-4">
                          <Triangle direction="left" />
                          <Triangle direction="right" />
                        </Column>
                        <Column className="min-h-40 w-1/2 flex-1">
                          <ColumnHeader>Zuordenbare {label}</ColumnHeader>
                          <VirtualizedListServerDriven
                            className={
                              hasNoMatchingSelectableItems ? 'h-0' : 'h-96'
                            }
                            fetchNextPage={fetchNextPageSelectableItems}
                            hasNextPage={hasNextPageSelectableItems}
                            isFetchingNextPage={
                              isFetchingNextPageSelectableItems
                            }
                            itemHeight={48}
                            items={selectableItemsFiltered}
                            renderItem={(item) => renderSelectableItem(item)}
                            totalCount={selectableItemsFiltered.length}
                          />
                          {hasNoMatchingSelectableItems ? (
                            <NoResults searchString={searchString} />
                          ) : null}
                        </Column>
                      </>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      );
    },
  ),
  'Daten konnten nicht geladen werden.',
);
